/**
 * Question Helpers
 * 
 * Utility functions and constants for question types in the vetting app.
 * This provides a centralized location for question type labels and helper functions.
 */

/**
 * Question type display labels for candidate-facing UI
 */
export const QUESTION_TYPE_LABELS = {
  'multiple-answer': 'Select All The Apply',
  'single-answer': 'Select A Single Option',
  'long-text': 'Provide A Detailed Answer',
  'short-text': 'Provide A Brief Answer',
  'true-false': 'True Or False',
  'audio-answer': 'Record Your Answer',
  'video-answer': 'Answer With Video',
  'code-completion': 'Complete This Code',
  'code-explanation': 'Explain This Code',
  'code-refactoring': 'Refactor This Code',
  'code-write': 'Write Code',
  'find-the-bug': 'Find The Bug',
  'security-task': 'Security Task',
  'performance-optimization': 'Performance Optimization Task',
};

/**
 * Get display label for a question type
 * @param {string} type - Question type code
 * @returns {string} Display label for the question type
 */
export const getQuestionTypeLabel = (type) => {
  return QUESTION_TYPE_LABELS[type] || type;
};

/**
 * Check if question type requires code snippet display
 * @param {string} questionType - Question type code
 * @returns {boolean} True if question type requires code snippet
 */
export const requiresCodeSnippet = (questionType) => {
  return [
    'code-completion',
    'code-explanation', 
    'code-refactoring',
    'code-write',
    'find-the-bug',
    'security-task',
    'performance-optimization'
  ].includes(questionType);
};

/**
 * Check if question type has multiple choice options
 * @param {string} questionType - Question type code
 * @returns {boolean} True if question type has options
 */
export const hasOptions = (questionType) => {
  return ['multiple-answer', 'single-answer', 'true-false'].includes(questionType);
};

/**
 * Check if question type is a code-based question
 * @param {string} questionType - Question type code
 * @returns {boolean} True if question type is code-based
 */
export const isCodeQuestion = (questionType) => {
  return requiresCodeSnippet(questionType);
};

/**
 * Check if question type requires text input
 * @param {string} questionType - Question type code
 * @returns {boolean} True if question type requires text input
 */
export const requiresTextInput = (questionType) => {
  return ['long-text', 'short-text'].includes(questionType);
};

/**
 * Check if question type is audio-based
 * @param {string} questionType - Question type code
 * @returns {boolean} True if question type is audio-based
 */
export const isAudioQuestion = (questionType) => {
  return questionType === 'audio-answer';
};
